import React from "react";
import styled from "styled-components";

import { Grid, Toolbar } from "@examspark/common-ui";
import { PilledJumpBar } from "@examspark/common-ui/dist/components/utils/JumpBar";
import { ParsedTCMPCUnit } from "@examspark/types";

import ContentSection, {
  ContentSectionSecondary,
} from "../common/ContentSection";
import UnitContent, { TCRUnitContent } from "../common/UnitContent";

import { Annotations } from "./Annotations";
import BundleTable from "./BundleTable";
import ElpsTable from "./ElpsTable";
import UnitObjectives from "./UnitObjectives";
import UnitResources from "./UnitResources";
import UnitVocabulary from "./UnitVocabulary";
// import UnitTeks from "./UnitTeks";

const Content = styled.article`
  flex: 1;
  flex-basis: 80ch;

  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const TitleRow = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  & > svg {
    flex-shrink: 0;
  }
`;

const JumpToTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: normal;
  color: #555;
  text-transform: uppercase;
`;

const MisconceptionsList = styled(Grid)`
  grid-template-columns: repeat(auto-fill, minmax(25rem, 1fr));
`;

const MisconceptionItem = styled.div`
  background-color: #f7f7f7;
  border-radius: 1rem;
  color: #666;
  padding: 1rem;
  font-size: 1.1rem;
  line-height: 1.5em;
`;

const JumpBox = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

type UnitPrimaryProps = {
  unit: ParsedTCMPCUnit;
  header: React.ReactNode;
};
const UnitPrimary = ({ unit: currentUnit, header }: UnitPrimaryProps) => {
  const hasOverviewContent = !!(
    currentUnit.data?.introduction ||
    currentUnit.data?.priorToUnit ||
    currentUnit.data?.inquiry ||
    currentUnit.data?.duringUnit ||
    currentUnit.data?.afterUnit ||
    currentUnit.data?.additionalNotes
  );

  // hack: give "notes" special treatment. We will know that this is where we're
  // putting the lessons table in a TCR document
  // const isTCRUnit =
  //   !!currentUnit.bankId?.match(/tcr/) &&
  //   !!currentUnit.workspaceId?.match(/tcmpc/);
  // console.log("isTCRUnit", isTCRUnit);

  return (
    <Content>
      <TitleRow>{header}</TitleRow>
      <JumpBox>
        <JumpToTitle>Jump To Section</JumpToTitle>
        <PilledJumpBar />
      </JumpBox>
      {hasOverviewContent && (
        <ContentSection
          title="Overview"
          actions={
            <Annotations locationKey="Introduction" resource={currentUnit} />
          }
        >
          {currentUnit.data?.introduction && (
            <ContentSectionSecondary title="Introduction">
              <UnitContent content={currentUnit.data?.introduction} />
            </ContentSectionSecondary>
          )}
          {currentUnit.data?.priorToUnit && (
            <ContentSectionSecondary title="Prior to this Unit / Module">
              <UnitContent content={currentUnit.data.priorToUnit} />
            </ContentSectionSecondary>
          )}
          {currentUnit.data?.inquiry && (
            <ContentSectionSecondary title="Inquiry in this Unit / Module">
              <UnitContent content={currentUnit.data.inquiry} />
            </ContentSectionSecondary>
          )}
          {currentUnit.data?.duringUnit && (
            <ContentSectionSecondary title="During this Unit / Module">
              <UnitContent content={currentUnit.data.duringUnit} />
            </ContentSectionSecondary>
          )}

          {currentUnit.data?.afterUnit && (
            <ContentSectionSecondary title="After this Unit / Module">
              <UnitContent content={currentUnit.data.afterUnit} />
            </ContentSectionSecondary>
          )}

          {currentUnit.data?.additionalNotes && (
            <ContentSectionSecondary title="Additional Notes">
              <UnitContent content={currentUnit.data.additionalNotes} />
            </ContentSectionSecondary>
          )}

          {currentUnit.data?.suggestedTools && (
            <ContentSectionSecondary title="Suggested Tools & Representations">
              <TCRUnitContent content={currentUnit.data.suggestedTools} />
            </ContentSectionSecondary>
          )}
        </ContentSection>
      )}

      {!!currentUnit.data?.misconceptions?.length && (
        <ContentSection
          title="Misconceptions"
          actions={
            <Annotations locationKey="Misconceptions" resource={currentUnit} />
          }
        >
          <MisconceptionsList>
            {currentUnit.data.misconceptions.map((misconception) => (
              <MisconceptionItem key={misconception.id}>
                <UnitContent content={misconception.text} />
              </MisconceptionItem>
            ))}
          </MisconceptionsList>
        </ContentSection>
      )}

      {!!currentUnit.data?.underdevelopedConcepts?.length && (
        <ContentSection
          title="Underdeveloped Concepts"
          actions={
            <Annotations
              locationKey="Underdeveloped Concepts"
              resource={currentUnit}
            />
          }
        >
          <MisconceptionsList>
            {currentUnit.data.underdevelopedConcepts.map((misconception) => (
              <MisconceptionItem key={misconception.id}>
                <UnitContent content={misconception.text} />
              </MisconceptionItem>
            ))}
          </MisconceptionsList>
        </ContentSection>
      )}

      <UnitVocabulary unit={currentUnit} />

      {currentUnit.data?.tcrContent && (
        <ContentSection
          title="TCR Content"
          actions={
            <Annotations locationKey="TCR Content" resource={currentUnit} />
          }
        >
          <TCRUnitContent content={currentUnit.data.tcrContent} />
        </ContentSection>
      )}

      {currentUnit.data &&
        currentUnit.data.subUnits.map((subUnit, index) => (
          <ContentSection
            key={index}
            title={
              currentUnit.data?.subUnits.length === 1
                ? "PA Bundle"
                : `PA Bundle ${index + 1}`
            }
            actions={
              <Annotations
                locationKey={`Bundle ${index + 1}`}
                resource={currentUnit}
              />
            }
          >
            <BundleTable subUnit={subUnit} unit={currentUnit} />
          </ContentSection>
        ))}

      <UnitResources unit={currentUnit} />

      {currentUnit.objectives && (
        <ContentSection
          title="Taught TEKS"
          actions={
            <Toolbar>
              <Annotations locationKey="Taught TEKS" resource={currentUnit} />
            </Toolbar>
          }
        >
          <UnitObjectives unit={currentUnit} />
        </ContentSection>
      )}

      {currentUnit.language?.includes("EN") && (
        <ContentSection title="English Language Proficiency Standards">
          <ElpsTable />
        </ContentSection>
      )}

      {currentUnit.data?.research && (
        <ContentSection
          title="Research"
          actions={
            <Annotations locationKey="Research" resource={currentUnit} />
          }
        >
          <UnitContent content={currentUnit.data.research} />
        </ContentSection>
      )}

      {currentUnit.data?.spiralingSuggestions && (
        <ContentSection
          title="Spiraling Suggestions"
          actions={
            <Annotations
              locationKey="Spiraling Suggestions"
              resource={currentUnit}
            />
          }
        >
          <UnitContent content={currentUnit.data.spiralingSuggestions} />
        </ContentSection>
      )}
    </Content>
  );
};

export default UnitPrimary;
